import { useCallback } from "react";
import { ariaAttributes, dataAttributes } from "../../utils/dom";
import visuallyHidden from "../../utils/visuallyHidden";

/**
 * A react hook for state management for a `Radio` component. This hook
 * is consumed by the `Radio` component.
 */

export default function useRadio(props) {
  const {
    isChecked,
    isDisabled,
    isInvalid,
    isRequired,
    name,
    onChange: onChangeProp,
    value,
    "data-radiogroup": dataRadioGroup,
    ...restProps
  } = props;

  const handleChange = useCallback(
    (e) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }

      onChangeProp?.(e);
    },
    [isDisabled, onChangeProp]
  );

  /**
   * Returns props for the native input element. To use, spread
   * onto the target input.
   */
  const getInputProps = useCallback(
    (passedProps = {}) => ({
      ...passedProps,
      "aria-invalid": ariaAttributes(isInvalid),
      checked: !!isChecked,
      "data-disabled": dataAttributes(isDisabled),
      "data-radiogroup": dataRadioGroup,
      disabled: isDisabled,
      name,
      onChange: handleChange,
      style: visuallyHidden,
      type: "radio",
      value,
    }),
    [
      isInvalid,
      isDisabled,
      handleChange,
      isChecked,
      value,
      name,
      dataRadioGroup,
    ]
  );

  /**
   * Returns props for the label element. To use, spread
   * onto the target label.
   */
  const getLabelProps = useCallback(
    (passedProps = {}) => ({
      ...passedProps,
      "data-checked": dataAttributes(isChecked),
      "data-disabled": dataAttributes(isDisabled),
      "data-invalid": dataAttributes(isInvalid),
    }),
    [isChecked, isDisabled, isInvalid]
  );

  /**
   * Returns props that can be passed to a custom radio element
   * to help mimic the functionality of a radio button.
   * Named `getCheckboxProps` to avoid clash with `getRadioProps`
   * in `use-radio-group.js`
   */
  const getCheckboxProps = useCallback(
    (passedProps = {}) => ({
      ...passedProps,
      "aria-hidden": true,
      "data-checked": dataAttributes(isChecked),
      "data-disabled": dataAttributes(isDisabled),
      "data-invalid": dataAttributes(isInvalid),
    }),
    [isChecked, isDisabled, isInvalid]
  );

  return {
    getCheckboxProps,
    getInputProps,
    getLabelProps,
    restProps,
    state: {
      isChecked,
      isDisabled,
      isInvalid,
      isRequired,
    },
  };
}
